import _ from "lodash";
import React, { useMemo } from "react";

import classNames from "classnames";
import { connect } from "react-redux";
import { useI18n } from "Hooks";

import { Tooltip, IconButton, Button, EmptyState } from "@toddle-edu/ds-web";
import { MinusCircleOutlined, ChevronUpOutlined } from "@toddle-edu/ds-icons";
import MessagingAvatar from "MessagingV2/components/ThreadList/Avatar/Avatar";
import StudentAlerts from "AppComponents/StudentAlerts";
import {
  getDefaultNameSortPreference,
  getDefaultStudentNameOrderPreference,
} from "modules/CommonSelectors";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import { PARENT, STAFF, STUDENT } from "Constants/stringConstants";

import {
  SUB_TEXT_LENGTH,
  getFormattedNameForUserType,
  getSubText,
} from "MessagingV2/modules/Utils";
import { isIdEqual } from "Utils";

import classes from "./ViewMembers.scss";
import { useMetadata } from "../MetadataProvider";
import {
  ENTITY_TYPE,
  getUserCountByEntity,
  mapSharedWithToMetadata,
} from "Announcements/modules/utils";

const userTypeLabelsPlural = {
  [STUDENT]: "common:student_plural",
  [PARENT]: "common:family_member_plural",
  [STAFF]: "common:staff_members_plural",
};

const customAvatarStyle = {
  size: "medium",
};

const UserTypeCard = ({
  onRemoveClick,
  userId,
  mainText,
  subText,
  profileImage,
  isRevampEnabled,
  testId,
  isStudentType,
  alertTypes,
}) => {
  const componentTestId = testId ? `${testId}-userTypeCard` : undefined;
  const { t } = useI18n();
  const mainTextClasses = classNames({
    [classes.title]: !isRevampEnabled,
    [classes.titleRevamp]: isRevampEnabled,
    "text-body-1": !isRevampEnabled,
    "text-body": isRevampEnabled,
  });
  const subTextClasses = classNames({
    [classes.userSubTitle]: !isRevampEnabled,
    [classes.userSubTitleRevamp]: isRevampEnabled,
    "text-body-2": !isRevampEnabled,
    "text-body-s": isRevampEnabled,
  });
  return (
    <div className={classes.userTypeCardContainer}>
      <MessagingAvatar
        id={userId}
        name={mainText}
        url={profileImage}
        customAvatarStyles={customAvatarStyle}
      />
      <div className={classes.userInfoContainer}>
        <div className={classes.titleContainer}>
          <div className={mainTextClasses}>{mainText}</div>
          {isStudentType ? (
            <StudentAlerts studentId={userId} alertTypes={alertTypes} />
          ) : null}
        </div>
        {subText ? (
          <ConditionalWrapper
            wrapper={<Tooltip tooltip={subText} />}
            condition={_.size(subText) > SUB_TEXT_LENGTH}
            type={"wrapperAsElement"}
          >
            <div className={subTextClasses}>
              {_.truncate(subText, { length: SUB_TEXT_LENGTH })}
            </div>
          </ConditionalWrapper>
        ) : null}
      </div>
      <div className={classes.iconButtonContainer}>
        <Tooltip
          tooltip={t("common:remove_with_label", {
            label: t("common:recipient"),
          })}
          placement="top"
        >
          <IconButton
            dsVersion="2.0"
            icon={<MinusCircleOutlined />}
            variant="neutral"
            type="plain"
            size="medium"
            onClick={onRemoveClick}
            className={classes.removeIcon}
            testId={`${componentTestId}-${userId}`}
          />
        </Tooltip>
      </div>
    </div>
  );
};

const UserTypeCards = ({
  users,
  onRemoveUsers,
  defaultStudentNameSortPreference,
  defaultStudentNameOrderPreference,
  loggedInUserType,
  loggedInUserId,
  isRevampEnabled,
  showStudentId,
  testId,
}) => {
  const { t } = useI18n(["common"]);

  const componentTestId = testId ? `${testId}-userTypeCards` : undefined;
  return _.map(users, user => {
    const isCurrentLoggedInUser = isIdEqual(loggedInUserId, user.id);

    const handleRemoveUser = () => onRemoveUsers([user]);

    const { id, profileImage, type, alertTypes = [] } = user;

    let name = getFormattedNameForUserType({
      user,
      orderBy: defaultStudentNameSortPreference,
      defaultStudentNameOrderPreference,
      isFormattedNameV2: true,
    });

    if (isCurrentLoggedInUser) {
      name = t("common:you_with_full_name", { fullName: name });
    }

    const studentId = _.get(user, "sourceId", "");

    return (
      <UserTypeCard
        key={id}
        userId={id}
        mainText={name}
        subText={getSubText({
          loggedInUserRole: loggedInUserType,
          messagedUserObj: user,
          messagedUserRole: _.get(user, "type"),
          t,
          defaultStudentNameSortPreference,
          showStudentId:
            showStudentId && type === STUDENT && !_.isEmpty(studentId),
          studentId,
        })}
        profileImage={profileImage}
        onRemoveClick={handleRemoveUser}
        isRevampEnabled={isRevampEnabled}
        testId={componentTestId}
        alertTypes={alertTypes}
        isStudentType={type === STUDENT}
      />
    );
  });
};

const UserTypeList = ({
  users,
  type,
  defaultStudentNameSortPreference,
  defaultStudentNameOrderPreference,
  loggedInUserType,
  loggedInUserId,
  onRemoveUsers,
  isRevampEnabled,
  showStudentId = false,
  testId,
}) => {
  const [open, setOpen] = React.useState(true);

  const componentTestId = testId ? `${testId}-userTypeList` : undefined;
  const toggleAccordion = () => setOpen(open => !open);

  const { t } = useI18n(["common"]);

  if (_.isEmpty(users)) {
    return null;
  }
  const labelWithCount = `${t(userTypeLabelsPlural[type])} (${_.size(users)})`;

  const userTypeTitleClasses = classNames(
    classes.userTypeTitle,
    "text-label-3",
    {
      [classes.userTypeTitleRevamped]: isRevampEnabled,
    }
  );

  return (
    <div className={classes.userTypeList}>
      <div
        className={userTypeTitleClasses}
        onClick={toggleAccordion}
        data-test-id={`${componentTestId}-toggleAccordion-button`}
      >
        {labelWithCount}
        <IconButton
          dsVersion="2.0"
          variant="neutral"
          type="plain"
          size="medium"
          icon={<ChevronUpOutlined />}
          className={classNames(classes.accordion, {
            [classes.accordionClose]: !open,
          })}
        />
      </div>
      {open ? (
        <UserTypeCards
          users={users}
          onRemoveUsers={onRemoveUsers}
          defaultStudentNameSortPreference={defaultStudentNameSortPreference}
          defaultStudentNameOrderPreference={defaultStudentNameOrderPreference}
          loggedInUserType={loggedInUserType}
          loggedInUserId={loggedInUserId}
          isRevampEnabled={isRevampEnabled}
          showStudentId={showStudentId}
          testId={componentTestId}
        />
      ) : null}
    </div>
  );
};

const mapStateToProps = state => {
  const defaultStudentNameSortPreference = getDefaultNameSortPreference(state);
  const defaultStudentNameOrderPreference = getDefaultStudentNameOrderPreference(
    state
  );
  const { user_type: loggedInUserType, id: loggedInUserId } = getUserInfo();

  return {
    defaultStudentNameSortPreference,
    defaultStudentNameOrderPreference,
    loggedInUserType,
    loggedInUserId,
  };
};

const UserTypeListContainer = connect(mapStateToProps)(UserTypeList);

const ViewMembers = props => {
  const {
    users,
    onRemoveUsers,
    handleClearAll,
    showClearAllBtn,
    isRevampEnabled,
    sharedWith,
    showStudentId,
    showEmptyStateForMembersList,
    testId,
  } = props;
  const { t } = useI18n(["common"]);
  const {
    countUsersSelectedText,
    selectedUsersListText,
    viewMembersEmptyStateTitle,
    viewMembersEmptyStateSubtitle,
    viewMembersEmptyStateIllustration,
  } = useMetadata();
  const componentTestId = testId ? `${testId}-viewMembers` : undefined;
  const usersByType = React.useMemo(() => {
    return _.reduce(
      users,
      (acc, user) => {
        if (!acc[user.type]) {
          acc[user.type] = [];
        }
        acc[user.type].push(user);
        return acc;
      },
      {}
    );
  }, [users]);

  const headerClasses = classNames({
    [classes.headerTitle]: !isRevampEnabled,
    "heading-6": !isRevampEnabled,
    [classes.flex]: showClearAllBtn,
    "text-label": isRevampEnabled,
  });

  const subtitleClasses = classNames({
    [classes.headerSubtitle]: !isRevampEnabled,
    "text-label-4": !isRevampEnabled,
    "text-body-s": isRevampEnabled,
    [classes.headerSubtitleV3]: isRevampEnabled,
  });

  const headerContainerClasses = classNames(classes.headerContainer, {
    [classes.headerContainerRevamp]: isRevampEnabled,
  });

  const membersContainerClasses = classNames(classes.membersContainer, {
    [classes.membersContainerRevamped]: isRevampEnabled,
  });

  const userCountByEntity = useMemo(
    () =>
      getUserCountByEntity({
        entityType: ENTITY_TYPE.USER,
        users,
        selectedUserGroups: sharedWith,
        includeEmailCount: false,
      }),
    [users, sharedWith]
  );

  return (
    <div className={classes.rootContainer}>
      <div className={headerContainerClasses}>
        <div className={headerClasses}>
          {t(selectedUsersListText, { count: _.size(users) })}
          {showClearAllBtn && !_.isEmpty(users) ? (
            <Button
              dsVersion="2.0"
              variant="progressive"
              type="plain"
              size="small"
              className={classes.clearAllButton}
              onClick={handleClearAll}
              testId={componentTestId}
            >
              {t("announcement:clear_all")}
            </Button>
          ) : null}
        </div>
        <div className={subtitleClasses}>
          {!isRevampEnabled
            ? t(countUsersSelectedText, {
                count: _.size(users),
              })
            : _.map(sharedWith, (group, index) => (
                <React.Fragment key={group}>
                  <div className={classes.userCountInnerContainer}>
                    {userCountByEntity[mapSharedWithToMetadata[group].countKey]}
                    <div>
                      {_.toLower(t(mapSharedWithToMetadata[group].countLabel))}
                    </div>
                  </div>
                  {index < _.size(sharedWith) - 1 ? (
                    <div className={classes.divider} />
                  ) : null}
                </React.Fragment>
              ))}
        </div>
      </div>
      {_.isEmpty(users) && showEmptyStateForMembersList ? (
        <EmptyState
          dsVersion="2.0"
          title={t(viewMembersEmptyStateTitle)}
          subtitle={t(viewMembersEmptyStateSubtitle)}
          illustration={viewMembersEmptyStateIllustration}
        />
      ) : (
        <div className={membersContainerClasses}>
          <UserTypeListContainer
            users={usersByType[STUDENT]}
            type={STUDENT}
            onRemoveUsers={onRemoveUsers}
            isRevampEnabled={isRevampEnabled}
            showStudentId={showStudentId}
            testId={componentTestId}
          />
          <UserTypeListContainer
            users={usersByType[STAFF]}
            type={STAFF}
            onRemoveUsers={onRemoveUsers}
            isRevampEnabled={isRevampEnabled}
            testId={componentTestId}
          />
          <UserTypeListContainer
            users={usersByType[PARENT]}
            type={PARENT}
            onRemoveUsers={onRemoveUsers}
            isRevampEnabled={isRevampEnabled}
            testId={componentTestId}
          />
        </div>
      )}
    </div>
  );
};

export default ViewMembers;
